
export interface CefResponse {
    status: number;
    payload: any;
}

type CefType = "GETTER" | "SETTER" | "ACTION";

const CEF_TIMEOUT_MS = 15000;

// Single source of truth for CEF calls: rejects on failure, invalid JSON and timeout
// so callers can never stay stuck waiting on a call that will never answer.
export function cefRequest(type: CefType, name: string, payload: any): Promise<CefResponse> {
    const w: any = window;

    return new Promise<CefResponse>((resolve, reject) => {
        if (!w.cefQuery) {
            reject(new Error("CEF is not available"));
            return;
        }

        let settled = false;
        const timer = setTimeout(() => {
            if (settled) {
                return;
            }
            settled = true;
            reject(new Error("CEF request timed out: " + name));
        }, CEF_TIMEOUT_MS);

        w.cefQuery({
            request: JSON.stringify({
                type: type,
                name: name,
                payload: payload,
            }),
            persistent: false,
            onSuccess: (response: any) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                try {
                    resolve(JSON.parse(response));
                } catch (e) {
                    reject(new Error("Invalid CEF response for " + name + ": " + e));
                }
            },
            onFailure: (errorCode: any, errorMessage: any) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timer);
                reject(new Error("CEF failure for " + name + " (" + errorCode + "): " + errorMessage));
            },
        });
    });
}

function bridge(type: CefType, name: string, payload: any, onSuccess?: (data: CefResponse) => void): Promise<CefResponse> {
    const request = cefRequest(type, name, payload);
    request
        .then((data) => {
            if (onSuccess) {
                onSuccess(data);
            }
        })
        .catch((error) => console.error("CEF ERROR: " + name + " - " + error));
    return request;
}

// CEF Request for Getter
export function requestGetter(name: string, payload: any, onSuccess?: (data: CefResponse) => void): Promise<CefResponse> {
    return bridge("GETTER", name, payload, onSuccess);
}

// CEF Request for Setter
export function requestSetter(name: string, payload: any, onSuccess?: (data: CefResponse) => void): Promise<CefResponse> {
    return bridge("SETTER", name, payload, onSuccess);
}

// CEF Request for Action
export function requestAction(name: string, payload: any, onSuccess?: (data: CefResponse) => void): Promise<CefResponse> {
    return bridge("ACTION", name, payload, onSuccess);
}
